import type { ApiError } from '@qq/shared';
import { CLIENT_BUILD } from './build.js';

// Guarded so this module can be imported outside Vite - the question audit
// sheet server-renders the real components, and pulling in a hard
// import.meta.env read made that impossible.
const BASE = (import.meta.env?.VITE_API_BASE as string | undefined) ?? '/api';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message);
    this.name = 'ApiRequestError';
  }
}

/**
 * The only way this app talks to the server. Cookies are sent on every request
 * (sessions are httpOnly, so there is nothing for JavaScript to hold), and every
 * failure arrives as the shared ApiError shape rather than a thrown Response.
 */
export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      // Which build sent this, so the server's log can say which version each
      // iPad is really running - the only way to see an update land.
      'x-client-build': CLIENT_BUILD,
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const body = (parsed ?? {
      code: 'internal_error',
      message: 'Something went wrong.',
    }) as ApiError;
    throw new ApiRequestError(res.status, body);
  }
  return parsed as T;
}
